/**
 * Reference-list ordering that follows APA 7 (9.44) and MLA 9: alphabetize letter by letter,
 * ignoring spaces and punctuation, "nothing precedes something" (Brown, J. before Browning,
 * A.), a leading figure treated as if it were spelled out (1984 → Nineteen Eighty-Four), and
 * an undated work (n.d.) before dated ones by the same author.
 *
 * A comma (or the parenthesis opening the date) ends the unit being compared: the surname
 * is settled before initials are looked at, so "Brown, J." sorts before "Browning, A."
 * although "brownj" would follow "browni", and "Smith, J. (2020)" sorts before
 * "Smith, J., & Lee, K. (2018)": single-author entries come before multi-author ones.
 */
import { numberToWords } from "@/lib/tool-kit/style-checkers/number-words";

export type SortDirection = "asc" | "desc";

const ARTICLES = ["a", "an", "the"];
const RE_LEADING_NUMBER = /^\d+/;
const RE_NO_DATE = /\(n\.d\.(?:-[a-z])?\)/gi;
// A comma or the "(" that opens the date ends one unit of comparison.
const RE_UNIT_BREAK = /[,(]/;
const RE_NOT_LETTER_OR_DIGIT = /[^\p{L}\p{N}]+/gu;

export function stripArticle(text: string): string {
  const trimmed = text.trim();
  const lower = trimmed.toLowerCase();
  for (const article of ARTICLES) {
    if (lower.startsWith(`${article} `)) {
      return trimmed.slice(article.length + 1);
    }
  }
  return trimmed;
}

/** 1984 → "nineteen eighty-four" (a year-like figure is read in pairs), 20 → "twenty". */
export function spokenNumber(value: number): string {
  if (value >= 1100 && value <= 1999) {
    const high = Math.floor(value / 100);
    const low = value % 100;
    if (low === 0) {
      return `${numberToWords(high)} hundred`;
    }
    const lowWords = low < 10 ? `oh ${numberToWords(low)}` : numberToWords(low);
    return `${numberToWords(high)} ${lowWords}`;
  }
  return numberToWords(value);
}

function spellLeadingNumber(text: string): string {
  const match = RE_LEADING_NUMBER.exec(text);
  if (!match) {
    return text;
  }
  const words = spokenNumber(Number(match[0]));
  return words ? `${words}${text.slice(match[0].length)}` : text;
}

/** The comma-separated units of an entry, each reduced to lower-case letters and digits. */
export function sortKey(entry: string, ignoreArticles: boolean): string[] {
  const text = ignoreArticles ? stripArticle(entry) : entry.trim();
  return spellLeadingNumber(text.replace(RE_NO_DATE, "(0)"))
    .split(RE_UNIT_BREAK)
    .map((unit) => unit.toLowerCase().replace(RE_NOT_LETTER_OR_DIGIT, ""));
}

const collator = new Intl.Collator(undefined, {
  sensitivity: "base",
  numeric: true,
});

function compareKeys(a: string[], b: string[]): number {
  const shared = Math.min(a.length, b.length);
  for (let i = 0; i < shared; i++) {
    const result = collator.compare(a[i], b[i]);
    if (result !== 0) {
      return result;
    }
  }
  return a.length - b.length;
}

export function sortEntries(
  entries: string[],
  direction: SortDirection,
  ignoreArticles: boolean
): string[] {
  const keyed = entries.map((entry) => ({
    entry,
    key: sortKey(entry, ignoreArticles),
  }));
  keyed.sort((a, b) => {
    const comparison = compareKeys(a.key, b.key);
    return direction === "asc" ? comparison : -comparison;
  });
  return keyed.map((item) => item.entry);
}
